import { useState, useEffect, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion as Motion, AnimatePresence } from "motion/react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { configHeader } from "@/features/evento/eventoThunks";
import ImageUploadInput from "./ImageUploadInput";
import DateTimeInput from "./DateTimeInput";
import TimezoneSelector from "./TimezoneSelector";
import { DateTime } from "luxon";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";

// Utils
import { formatFechaHoraEvento, scrollToTop } from "@/lib/utilsUI";

function parseUTCValue(val, tz) {
  if (!val) return null;
  let dt = DateTime.fromSQL(val, { zone: "utc" });
  if (dt.isValid) return dt.setZone(tz);
  dt = DateTime.fromISO(val, { zone: "utc" });
  return dt.isValid ? dt.setZone(tz) : null;
}

const QUICK_JUMPS = [60, 120, 180, 240, 360, 480];

function MinutesOffsetStepper({ label, description, value, onChange, eventStart, timezone, minOffset = 5 }) {
  const tz = timezone || DEFAULT_TIMEZONE;

  const startDT = useMemo(() => parseUTCDate(eventStart, tz), [eventStart, tz]);
  const valueDT = useMemo(() => parseUTCValue(value, tz), [value, tz]);

  const offsetMinutes = useMemo(() => {
    if (!valueDT || !startDT) return null;
    return Math.round(valueDT.diff(startDT, "minutes").minutes);
  }, [valueDT, startDT]);

  const computedDisplay = useMemo(() => {
    if (!valueDT) return null;
    return valueDT.toFormat("dd/MM/yyyy HH:mm");
  }, [valueDT]);

  const formatOffset = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `+${m}min`;
    if (m === 0) return `+${h}h`;
    return `+${h}h ${m}min`;
  };

  const handleActivate = () => {
    if (!startDT) return;
    const activationOffset = Math.max(60, minOffset);
    const utcDT = startDT.plus({ minutes: activationOffset }).toUTC();
    onChange(utcDT.toFormat("yyyy-MM-dd'T'HH:mm"));
  };

  const handleStep = (delta) => {
    if (offsetMinutes === null || !startDT) return;
    const newOffset = offsetMinutes + delta;
    if (newOffset < minOffset) return;
    const utcDT = startDT.plus({ minutes: newOffset }).toUTC();
    onChange(utcDT.toFormat("yyyy-MM-dd'T'HH:mm"));
  };

  // Long press acceleration
  const holdRef = useRef(null);

  const startHold = (direction) => {
    if (offsetMinutes === null || !startDT) return;
    const startTime = Date.now();
    let acc = offsetMinutes;
    holdRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const step = elapsed > 3000 ? 60 : elapsed > 1500 ? 15 : 5;
      acc += direction * step;
      if (acc < minOffset) { acc = minOffset; return; }
      onChange(startDT.plus({ minutes: acc }).toUTC().toFormat("yyyy-MM-dd'T'HH:mm"));
    }, 150);
  };

  const stopHold = () => {
    clearInterval(holdRef.current);
    holdRef.current = null;
  };

  return (
    <div>
      <label className="block text-gray-300 text-xs font-semibold mb-0.5">{label}</label>
      <p className="text-gray-500 text-xs mb-2">{description}</p>
      {offsetMinutes === null ? (
        <div className="flex items-center justify-between bg-aw-bluedark-95 border border-gray-600 rounded-lg px-3 py-2">
          <span className="text-gray-500 text-sm italic">Sin finalización configurada</span>
          <button
            type="button"
            onClick={handleActivate}
            disabled={!startDT}
            className="text-aw-green text-xs font-semibold hover:underline disabled:opacity-40"
          >
            Configurar
          </button>
        </div>
      ) : (
        <div className="bg-aw-bluedark-95 border border-gray-600 rounded-lg px-3 py-2 space-y-1">
          {/* Quick jump chips */}
          <div className="flex gap-1 flex-wrap justify-center pb-1">
            {QUICK_JUMPS.filter((m) => m >= minOffset).map((mins) => {
              const isActive = offsetMinutes === mins;
              return (
                <button
                  key={mins}
                  type="button"
                  onClick={() => {
                    if (!startDT) return;
                    onChange(startDT.plus({ minutes: mins }).toUTC().toFormat("yyyy-MM-dd'T'HH:mm"));
                  }}
                  className={`px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                    isActive
                      ? "bg-aw-green text-aw-bluedark"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  +{mins / 60}h
                </button>
              );
            })}
          </div>

          {/* +/- stepper with long press */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleStep(-5)}
              onMouseDown={() => startHold(-1)}
              onMouseUp={stopHold}
              onMouseLeave={stopHold}
              onTouchStart={() => startHold(-1)}
              onTouchEnd={stopHold}
              disabled={offsetMinutes - 5 < minOffset}
              className="w-7 h-7 rounded bg-gray-700 text-white text-sm font-bold flex items-center justify-center disabled:opacity-40 hover:bg-gray-600 select-none"
            >
              −
            </button>
            <span className="text-white text-sm font-semibold flex-1 text-center">
              {formatOffset(offsetMinutes)} desde el inicio
            </span>
            <button
              type="button"
              onClick={() => handleStep(5)}
              onMouseDown={() => startHold(1)}
              onMouseUp={stopHold}
              onMouseLeave={stopHold}
              onTouchStart={() => startHold(1)}
              onTouchEnd={stopHold}
              className="w-7 h-7 rounded bg-gray-700 text-white text-sm font-bold flex items-center justify-center hover:bg-gray-600 select-none"
            >
              +
            </button>
          </div>
          {computedDisplay && (
            <p className="text-aw-green/80 text-xs text-center">{computedDisplay}</p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-red-400 text-xs hover:underline"
            >
              Quitar finalización
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function LockedField({ children }) {
  return (
    <div className="relative group">
      <div className="pointer-events-none opacity-40 select-none">
        {children}
      </div>
      <div className="absolute inset-0 cursor-not-allowed z-10" />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 border border-gray-700 text-gray-200 text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20">
        Estas opciones no se pueden editar si el evento ya ha sido publicado
      </div>
    </div>
  );
}

export default function CardEvento({ initialData = {}, onSave }) {
  const dispatch = useDispatch();
  const { idEvento } = useParams();
  const { isLoading: eventoLoading } = useSelector((state) => state.evento);

  // Estados locales para UI
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [imageUpdated, setImageUpdated] = useState(false); // Estado temporal para indicar si la imagen cambió
  const [imageError, setImageError] = useState(false);

  // Obtener zona horaria del evento (o usar default)
  const eventTimezone = initialData.zonaHoraria || DEFAULT_TIMEZONE;
  const isPublished = !!initialData?.published;

  // Backend envía fechas en UTC: "2026-01-25 18:30:00"
  // parseUTCDate interpreta como UTC y convierte a timezone del evento
  const fechaInicioDT = initialData.fechaHoraInicio 
    ? parseUTCDate(initialData.fechaHoraInicio, eventTimezone)
    : null;
  
  const fechaFinDT = initialData.fechaHoraFin 
    ? parseUTCDate(initialData.fechaHoraFin, eventTimezone)
    : null;

  // Extraer fecha y hora para los inputs
  const extractedDate = fechaInicioDT ? fechaInicioDT.toFormat('yyyy-MM-dd') : "";
  const extractedTime = fechaInicioDT ? fechaInicioDT.toFormat('HH:mm') : "";
  const extractedDateFin = fechaFinDT ? fechaFinDT.toFormat('yyyy-MM-dd') : "";
  const extractedTimeFin = fechaFinDT ? fechaFinDT.toFormat('HH:mm') : "";

  const [formData, setFormData] = useState({
    img: initialData.imgEvento || "",
    date: extractedDate || "",
    time: extractedTime || "",
    dateFin: extractedDateFin || "",
    timeFin: extractedTimeFin || "",
    nombre: initialData.nombre || "",
    zonaHoraria: eventTimezone, // NUEVO: zona horaria del evento
    enPromocion: initialData.enPromocion || false,
    textoPromocion: initialData.textoPromocion || "",
  });

  const [previewData, setPreviewData] = useState({ ...formData });

  useEffect(() => {
    if (initialData) {
      // Recalcular en caso de que initialData cambie
      const eventTz = initialData.zonaHoraria || DEFAULT_TIMEZONE;
      const fechaIni = initialData.fechaHoraInicio ? parseUTCDate(initialData.fechaHoraInicio, eventTz) : null;
      const fechaFi = initialData.fechaHoraFin ? parseUTCDate(initialData.fechaHoraFin, eventTz) : null;

      const newData = {
        img: initialData.imgEvento || "",
        date: fechaIni ? fechaIni.toFormat('yyyy-MM-dd') : "",
        time: fechaIni ? fechaIni.toFormat('HH:mm') : "",
        dateFin: fechaFi ? fechaFi.toFormat('yyyy-MM-dd') : "",
        timeFin: fechaFi ? fechaFi.toFormat('HH:mm') : "",
        nombre: initialData.nombre || "",
        zonaHoraria: eventTz,
        enPromocion: initialData.enPromocion || false,
        textoPromocion: initialData.textoPromocion || "",
      };
      setFormData(newData);
      setPreviewData(newData);
    }
  }, [
    initialData,
    initialData.fechaHoraInicio,
    initialData.fechaHoraFin,
    initialData.zonaHoraria,
  ]);

  const handleInputChange = (field, value) => {
    // Si el campo es 'img' y hay un cambio, marcar imageUpdated como true
    if (field === "img" && value && value !== formData.img) {
      setImageUpdated(true);
    }

    // Crear el nuevo estado
    const newData = { ...formData, [field]: value };

    // Si se cambia la fecha o hora de inicio, validar y limpiar fecha fin si es necesario
    if (field === "date" || field === "time") {
      // Si se borraron los campos de inicio, limpiar también los de fin
      if (!value || !newData.date || !newData.time) {
        newData.dateFin = "";
        newData.timeFin = "";
      } else {
        // Si hay fecha fin establecida, validar que siga siendo válida
        if (newData.dateFin && newData.timeFin) {
          const startDateTime = new Date(`${newData.date}T${newData.time}:00`);
          const endDateTime = new Date(
            `${newData.dateFin}T${newData.timeFin}:00`
          );
          const startHour = parseInt(newData.time.split(":")[0]);

          let minEndDateTime;

          // Si la hora de inicio es 23:00 o posterior, el evento debe terminar el día siguiente a las 06:00
          if (startHour >= 23) {
            minEndDateTime = new Date(startDateTime);
            minEndDateTime.setDate(minEndDateTime.getDate() + 1);
            minEndDateTime.setHours(6, 0, 0, 0);
          } else {
            // Mismo día, 1 hora después
            minEndDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);
          }

          // Si la fecha fin es anterior a la mínima permitida, limpiar los campos
          if (endDateTime < minEndDateTime) {
            newData.dateFin = "";
            newData.timeFin = "";
          }
        }
      }
    }

    // Validaciones específicas para campos de fecha fin
    if (field === "dateFin" || field === "timeFin") {
      // Solo procesar si hay fecha y hora de inicio válidas
      if (newData.date && newData.time) {
        const startDateTime = new Date(`${newData.date}T${newData.time}:00`);
        const startHour = parseInt(newData.time.split(":")[0]);

        let minEndDateTime;
        let allowSameDay = true;

        // Si la hora de inicio es 23:00 o posterior, forzar día siguiente a las 06:00
        if (startHour >= 23) {
          minEndDateTime = new Date(startDateTime);
          minEndDateTime.setDate(minEndDateTime.getDate() + 1);
          minEndDateTime.setHours(6, 0, 0, 0);
          allowSameDay = false;
        } else {
          // Mismo día, 1 hora después
          minEndDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);
        }

        const minDate = `${minEndDateTime.getFullYear()}-${String(minEndDateTime.getMonth() + 1).padStart(2, '0')}-${String(minEndDateTime.getDate()).padStart(2, '0')}`;
        const minTime = `${String(minEndDateTime.getHours()).padStart(2, '0')}:${String(minEndDateTime.getMinutes()).padStart(2, '0')}`;

        // Validar fecha fin
        if (field === "dateFin") {
          if (value) {
            // Si no se permite el mismo día y se intenta usar el mismo día
            if (!allowSameDay && value === newData.date) {
              newData.dateFin = minDate;
              newData.timeFin = minTime;
              setFormData(newData);
              return;
            }
            // Si la fecha es anterior a la mínima
            if (value < minDate) {
              newData.dateFin = minDate;
              newData.timeFin = minTime;
              setFormData(newData);
              return;
            }
          }
        }

        // Validar hora fin
        if (field === "timeFin" && newData.dateFin) {
          // Si es el mismo día que el inicio y se permite mismo día
          if (newData.dateFin === newData.date && allowSameDay) {
            // La hora debe ser al menos 1 hora después del inicio
            const startMinutes =
              parseInt(newData.time.split(":")[0]) * 60 +
              parseInt(newData.time.split(":")[1]);
            const inputMinutes =
              parseInt(value.split(":")[0]) * 60 +
              parseInt(value.split(":")[1]);

            if (inputMinutes <= startMinutes) {
              const minHour = Math.floor((startMinutes + 60) / 60);
              const minMinute = (startMinutes + 60) % 60;
              newData.timeFin = `${minHour
                .toString()
                .padStart(2, "0")}:${minMinute.toString().padStart(2, "0")}`;
              setFormData(newData);
              return;
            }
          }
          // Si es la fecha mínima calculada y la hora es anterior
          else if (newData.dateFin === minDate && value && value < minTime) {
            newData.timeFin = minTime;
            setFormData(newData);
            return;
          }
        }
      }
    }

    setFormData(newData);
  };

  // Adaptar fecha inicio a UTC string para el stepper
  const eventStartUTC = (formData.date && formData.time)
    ? DateTime.fromISO(`${formData.date}T${formData.time}:00`, { zone: formData.zonaHoraria })
        .toUTC()
        .toSQL()
    : null;

  // Adaptar fecha fin a UTC ISO string para el stepper
  const fechaFinUTC = (formData.dateFin && formData.timeFin)
    ? DateTime.fromISO(`${formData.dateFin}T${formData.timeFin}:00`, { zone: formData.zonaHoraria })
        .toUTC()
        .toFormat("yyyy-MM-dd'T'HH:mm")
    : null;

  const handleSave = async () => {
    if (!idEvento) {
      console.error("No se encontró el ID del evento");
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);

    // Validate image is not empty when event is published
    if (isPublished && !formData.img) {
      setImageError(true);
      setIsSaving(false);
      return;
    }
    setImageError(false);

    try {
      // Preparar los datos para enviar al backend
      const configData = {
        nombre: formData.nombre,
        enPromocion: formData.enPromocion,
        textoPromocion: formData.textoPromocion,
        date: formData.date,
        time: formData.time,
        dateFin: formData.dateFin,
        timeFin: formData.timeFin,
        zonaHoraria: formData.zonaHoraria, // Incluir zona horaria
        img: imageUpdated ? formData.img?.file || formData.img : undefined, // Solo enviar imagen si cambió
        imageUpdated: imageUpdated, // Indicar explícitamente si la imagen cambió
      };

      console.log("Guardando configuración del header con datos:", {
        ...configData,
        img: configData.img instanceof File ? "File object" : configData.img,
        imageUpdated: imageUpdated,
      });

      // Llamar al thunk de Redux
      await dispatch(
        configHeader({
          uidEvento: idEvento,
          configData,
        })
      ).unwrap();

      // Actualizar el estado local con los nuevos datos
      setPreviewData({ ...formData });
      setIsEditMode(false);
      setSaveSuccess(true);
      setImageUpdated(false); // Resetear después de guardar exitosamente

      // Llamar a onSave si existe (callback adicional)
      if (onSave) {
        onSave(configData);
      }

      // Mostrar mensaje de éxito por unos segundos
      setTimeout(() => {
        // window.location.reload();
        setSaveSuccess(false);
      }, 500);
    } catch (error) {
      console.error("Error al guardar configuración del header:", error);
      // Aquí podrías mostrar un mensaje de error
      // Por ahora, solo mostramos en consola
    } finally {
      setIsSaving(false);
      scrollToTop();
    }
  };

  const handleCancel = () => {
    setFormData({ ...previewData });
    setImageUpdated(false); // Resetear estado de imagen modificada
    setIsEditMode(false);
    scrollToTop();
  };

  const formatDateTime = () => {
    if (!previewData.date) return "";
    
    // Crear DateTime en la zona horaria del evento (no en UTC ni local)
    const dt = DateTime.fromISO(
      `${previewData.date}T${previewData.time || "00:00"}:00`,
      { zone: previewData.zonaHoraria || DEFAULT_TIMEZONE }
    );
    
    return formatFechaHoraEvento(dt.toJSDate(), previewData.zonaHoraria || DEFAULT_TIMEZONE);
  };

  const handlePlayClick = () => {
    if (previewData.mediaLink) {
      window.open(previewData.mediaLink, "_blank");
    }
  };

  console.log("initialData.fechaHoraInicio:", initialData.fechaHoraInicio);
  console.log("extractedDate:", extractedDate);
  console.log("extractedTime:", extractedTime);

  return (
    <div className="relative bg-aw-bluedark-95 rounded-sm flex flex-col items-center w-full min-h-[60dvh] xl:min-h-[80dvh]">
      {/* Edit/Preview Toggle Button */}
      {isEditMode ? (
        <Motion.button
          whileHover={{ scale: isSaving ? 1 : 1.02 }}
          whileTap={{ scale: isSaving ? 1 : 0.98 }}
          onClick={handleSave}
          disabled={isSaving || eventoLoading}
          className={`absolute top-4 right-4 px-4 py-2 rounded-full bg-aw-green/80 text-aw-bluedark font-medium hover:bg-aw-green transition duration-300 flex items-center gap-2 z-[99] ${
            saveSuccess
              ? "bg-green-600 text-white"
              : isSaving || eventoLoading
              ? "bg-gray-500 text-gray-100 cursor-not-allowed"
              : "bg-aw-green/85 text-aw-bluedark hover:bg-aw-green"
          }`}
        >
          {isSaving || eventoLoading ? (
            <>
              <FontAwesomeIcon
                icon="fa-solid fa-spinner"
                className="animate-spin"
              />
              Guardando...
            </>
          ) : saveSuccess ? (
            <>
              <FontAwesomeIcon icon="fa-solid fa-check" />
              ¡Guardado!
            </>
          ) : (
            <>
              <FontAwesomeIcon icon="fa-solid fa-save" />
              Guardar Cambios
            </>
          )}
        </Motion.button>
      ) : (
        <Motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsEditMode(!isEditMode)}
          className="absolute top-4 right-4 px-4 py-2 rounded-full bg-aw-green/80 text-aw-bluedark font-medium hover:bg-aw-green transition duration-300 flex items-center gap-2 z-[99]"
        >
          <FontAwesomeIcon
            icon={isEditMode ? "fa-solid fa-eye" : "fa-solid fa-edit"}
          />
          {isEditMode ? "Vista Previa" : "Editar"}
        </Motion.button>
      )}

      <AnimatePresence mode="wait">
        {isEditMode ? (
          <Motion.div
            key="edit-mode"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="w-full px-4 py-16 space-y-6"
          >
            {/* Image Upload */}
            <ImageUploadInput
              value={formData.img}
              onChange={(value) => {
                setImageError(false);
                handleInputChange("img", value);
              }}
            />
            {imageError && (
              <p className="text-red-400 text-xs mt-1">
                La imagen es obligatoria. No puedes guardar el evento sin una imagen.
              </p>
            )}

            {/* Title Input */}
            <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">
                <FontAwesomeIcon icon="fa-solid fa-heading" className="mr-2" />
                Título del Evento
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => handleInputChange("nombre", e.target.value)}
                placeholder="Nombre del evento"
                className="w-full px-4 py-3 bg-aw-bluedark border border-gray-600 rounded-sm text-white placeholder-gray-500 focus:outline-none focus:border-aw-green transition duration-300"
              />
            </div>

            {isPublished ? (
              <LockedField>
                <h3 className="text-gray-100 mb-1">Zona Horaria del Evento</h3>
                <TimezoneSelector
                  value={formData.zonaHoraria}
                  onChange={(value) => handleInputChange("zonaHoraria", value)}
                  previewDate={formData.date}
                  previewTime={formData.time}
                />
              </LockedField>
            ) : (
              <>
                <h3 className="text-gray-100 mb-1">Zona Horaria del Evento</h3>
                {/* Timezone Selector */}
                <TimezoneSelector
                  value={formData.zonaHoraria}
                  onChange={(value) => handleInputChange("zonaHoraria", value)}
                  previewDate={formData.date}
                  previewTime={formData.time}
                />
              </>
            )}

            {isPublished ? (
              <LockedField>
                <h3 className="text-gray-100 mb-1 mt-4">Fecha y Hora de Inicio</h3>
                <DateTimeInput
                  date={formData.date}
                  time={formData.time}
                  onDateChange={(value) => handleInputChange("date", value)}
                  onTimeChange={(value) => handleInputChange("time", value)}
                  timezone={formData.zonaHoraria}
                />
              </LockedField>
            ) : (
              <>
                <h3 className="text-gray-100 mb-1 mt-4">Fecha y Hora de Inicio</h3>
                {/* Date and Time */}
                <DateTimeInput
                  date={formData.date}
                  time={formData.time}
                  onDateChange={(value) => handleInputChange("date", value)}
                  onTimeChange={(value) => handleInputChange("time", value)}
                  timezone={formData.zonaHoraria}
                />
              </>
            )}

            {isPublished ? (
              <LockedField>
                <h3 className="text-gray-100 mb-0">
                  Fecha y Hora de Finalización del Evento
                </h3>
                <span className="text-xs text-aw-green/50 block mb-2">
                  <b>Nota</b>: Este campo no se hará visible al usuario pero es
                  requerido, ya que determina el recuento entradas de todo el evento.
                </span>
                <MinutesOffsetStepper
                  label="Hora de finalización"
                  description="Cuánto tiempo después del inicio termina el evento. Mínimo 1 hora."
                  value={fechaFinUTC}
                  onChange={(utcISO) => {
                    if (!utcISO) {
                      setFormData((prev) => ({ ...prev, dateFin: "", timeFin: "" }));
                    } else {
                      const dt = DateTime.fromISO(utcISO, { zone: "utc" }).setZone(formData.zonaHoraria);
                      setFormData((prev) => ({
                        ...prev,
                        dateFin: dt.toFormat("yyyy-MM-dd"),
                        timeFin: dt.toFormat("HH:mm"),
                      }));
                    }
                  }}
                  eventStart={eventStartUTC}
                  timezone={formData.zonaHoraria}
                  minOffset={60}
                />
              </LockedField>
            ) : (
              <>
                <h3 className="text-gray-100 mb-0">
                  Fecha y Hora de Finalización del Evento
                </h3>
                <span className="text-xs text-aw-green/50 block mb-2">
                  <b>Nota</b>: Este campo no se hará visible al usuario pero es
                  requerido, ya que determina el recuento entradas de todo el evento.
                </span>
                <MinutesOffsetStepper
                  label="Hora de finalización"
                  description="Cuánto tiempo después del inicio termina el evento. Mínimo 1 hora."
                  value={fechaFinUTC}
                  onChange={(utcISO) => {
                    if (!utcISO) {
                      setFormData((prev) => ({ ...prev, dateFin: "", timeFin: "" }));
                    } else {
                      const dt = DateTime.fromISO(utcISO, { zone: "utc" }).setZone(formData.zonaHoraria);
                      setFormData((prev) => ({
                        ...prev,
                        dateFin: dt.toFormat("yyyy-MM-dd"),
                        timeFin: dt.toFormat("HH:mm"),
                      }));
                    }
                  }}
                  eventStart={eventStartUTC}
                  timezone={formData.zonaHoraria}
                  minOffset={60}
                />
              </>
            )}

            {/* Discount Toggle */}
            <div className="flex items-center justify-between p-4 bg-aw-bluedark border border-gray-600 rounded-sm">
              <div className="flex items-center gap-3">
                <FontAwesomeIcon
                  icon="fa-solid fa-tag"
                  className="text-aw-green text-xl"
                />
                <div>
                  <p className="text-white font-medium">Ticket en Descuento</p>
                  <p className="text-gray-400 text-sm">
                    Activar promoción especial
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enPromocion}
                  onChange={(e) =>
                    handleInputChange("enPromocion", e.target.checked)
                  }
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-aw-green"></div>
              </label>
            </div>

            {/* Subtitle (shown when discount is active) */}
            {formData.enPromocion && (
              <Motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
              >
                <label className="block text-sm font-medium text-gray-100 mb-2">
                  <FontAwesomeIcon
                    icon="fa-solid fa-percent"
                    className="mr-2"
                  />
                  Texto de Descuento
                </label>
                <input
                  type="text"
                  value={formData.textoPromocion}
                  onChange={(e) =>
                    handleInputChange("textoPromocion", e.target.value)
                  }
                  placeholder="Ej: 20% OFF"
                  className="w-full px-4 py-3 bg-aw-bluedark border border-gray-600 rounded-sm text-white placeholder-gray-500 focus:outline-none focus:border-aw-green transition duration-300"
                />
              </Motion.div>
            )}

            {/* Media Link - Esto no saldrá en esta versión */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-100 mb-2">
                <FontAwesomeIcon icon="fa-solid fa-link" className="mr-2" />
                Enlace de Medios (Video/Música)
              </label>
              <input
                type="url"
                value={formData.mediaLink}
                onChange={(e) => handleInputChange("mediaLink", e.target.value)}
                placeholder="https://ejemplo.com/media"
                className="w-full px-4 py-3 bg-aw-bluedark border border-gray-600 rounded-sm text-white placeholder-gray-500 focus:outline-none focus:border-aw-green transition duration-300"
              />
              <p className="text-xs text-gray-500 mt-1">
                Este enlace se abrirá al hacer clic en el botón de reproducir
              </p>
            </div> */}

            {/* Save and Cancel Buttons */}
            <div className="flex gap-3 pt-4">
              <Motion.button
                whileHover={{ scale: isSaving ? 1 : 1.02 }}
                whileTap={{ scale: isSaving ? 1 : 0.98 }}
                onClick={handleSave}
                disabled={isSaving || eventoLoading}
                className={`flex-1 py-3 font-bold rounded-sm transition duration-300 flex items-center justify-center gap-2 ${
                  saveSuccess
                    ? "bg-green-600 text-white"
                    : isSaving || eventoLoading
                    ? "bg-gray-500 text-gray-100 cursor-not-allowed"
                    : "bg-aw-green/85 text-aw-bluedark hover:bg-aw-green"
                }`}
              >
                {isSaving || eventoLoading ? (
                  <>
                    <FontAwesomeIcon
                      icon="fa-solid fa-spinner"
                      className="animate-spin"
                    />
                    Guardando...
                  </>
                ) : saveSuccess ? (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-check" />
                    ¡Guardado!
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-save" />
                    Guardar Cambios
                  </>
                )}
              </Motion.button>
              <Motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCancel}
                disabled={isSaving || eventoLoading}
                className={`flex-1 py-3 font-bold rounded-sm transition duration-300 flex items-center justify-center gap-2 ${
                  isSaving || eventoLoading
                    ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                    : "bg-gray-600 text-white hover:bg-gray-500"
                }`}
              >
                <FontAwesomeIcon icon="fa-solid fa-times" />
                Cancelar
              </Motion.button>
            </div>
          </Motion.div>
        ) : (
          <Motion.div
            key="preview-mode"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="relative bg-aw-bluedark-95 rounded-sm flex flex-col items-center w-full min-h-[60dvh] xl:min-h-[80dvh]"
          >
            {/* Background Blur Image */}
            {previewData.img && (
              <img
                src={previewData.img.preview || previewData.img}
                alt={previewData.nombre}
                className="absolute blur-lg backdrop-blur-3xl w-11/12 h-[40dvh] object-cover"
              />
            )}

            {/* Main Image */}
            {previewData.img && (previewData.img.preview || previewData.img) ? (
              <img
                src={previewData.img.preview || previewData.img}
                alt={previewData.nombre}
                className="rounded-sm w-full h-[40dvh] object-contain mb-4 z-50"
              />
            ) : (
              <div className="w-10/12 h-[40dvh] bg-aw-bluedark flex items-center justify-center border border-gray-600 m-4 rounded-2xl z-50">
                <div className="text-center text-gray-500">
                  <FontAwesomeIcon
                    icon="fa-solid fa-image"
                    className="text-6xl mb-3"
                  />
                  <p>Sin imagen</p>
                </div>
              </div>
            )}

            {/* Event Details */}
            <div className="px-4 py-4 sm:py-8 flex flex-col items-center">
              {previewData.date && (
                <Motion.h5
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-xs text-gray-400 mb-1"
                >
                  {/* Aquí se muestra la fecha de inicio del evento en el formato: "Domingo 25 de Enero - 15:30 hrs" */}
                  {formatDateTime()}
                </Motion.h5>
              )}

              {previewData.nombre && (
                <Motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-lg font-medium text-white text-center mb-1"
                >
                  {previewData.nombre}
                </Motion.h1>
              )}

              {previewData.enPromocion && previewData.textoPromocion && (
                <Motion.h4
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-aw-green font-bold text-xl mb-2"
                >
                  {previewData.textoPromocion}
                </Motion.h4>
              )}

              {/* Action Buttons */}
              <div className="flex justify-evenly w-full gap-3 mt-2">
                {/* <Motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handlePlayClick}
                  disabled={!previewData.mediaLink}
                  className={`w-10 h-10 rounded-full border border-white flex items-center justify-center text-white transition duration-300 ${
                    previewData.mediaLink
                      ? "bg-aw-bluedark hover:bg-aw-green hover:text-aw-bluedark cursor-pointer"
                      : "bg-gray-700 cursor-not-allowed opacity-50"
                  }`}
                >
                  <FontAwesomeIcon
                    icon="fa-solid fa-play"
                    className="text-lg"
                  />
                </Motion.button> */}

                {/* <Motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-10 h-10 rounded-full border border-white bg-aw-bluedark flex items-center justify-center text-white hover:bg-aw-green hover:text-aw-bluedark transition duration-300"
                >
                  <FontAwesomeIcon
                    icon="fa-solid fa-heart"
                    className="text-lg"
                  />
                </Motion.button> */}

                <Motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-10 h-10 rounded-full border border-white bg-aw-bluedark flex items-center justify-center text-white hover:bg-aw-green hover:text-aw-bluedark transition duration-300"
                >
                  <FontAwesomeIcon
                    icon="fa-solid fa-share-alt"
                    className="text-lg"
                  />
                </Motion.button>

                {/* <Motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-10 h-10 rounded-full border border-white bg-aw-bluedark flex items-center justify-center text-white hover:bg-aw-green hover:text-aw-bluedark transition duration-300"
                >
                  <FontAwesomeIcon
                    icon="fa-solid fa-users"
                    className="text-lg"
                  />
                </Motion.button> */}
              </div>
            </div>
          </Motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
